import type { RumEvent, RumEventDomainContext, RumInitConfiguration } from '@datadog/browser-rum';
import type { CountOptions, MeasureOptions, MetricAlert } from '@zg-rentals/monitor-base';
import type { Logger } from '@zg-rentals/logger-browser';
import { datadogRum } from '@datadog/browser-rum';
import { MonitorPlugin } from '@zg-rentals/monitor-base';

export type DatadogPluginConfig = {
  getMetricName?: (metric: string) => string;
  logger?: Logger;
  datadogOptions: RumInitConfiguration;
  alertConfig?: MetricAlert;
};

export class DatadogPlugin extends MonitorPlugin {
  getMetricName: (metric: string) => string;
  alertConfig?: MetricAlert;
  webVitals = {
    largest_contentful_paint: true,
    first_input_delay: true,
    cumulative_layout_shift: true,
    first_contentful_paint: true,
  };

  constructor({
    getMetricName = (metric) => `WebVital ${metric}`,
    logger,
    datadogOptions,
    alertConfig,
  }: DatadogPluginConfig) {
    super({
      pluginName: 'datadogPlugin',
      logger: logger,
    });

    this.validateRumInitConfig(datadogOptions);
    this.getMetricName = getMetricName;
    this.alertConfig = alertConfig;

    if (this.isDatadogInitialized()) {
      this.logger?.error('Failed to initialize DatadogPlugin - datadogRum is already initialized.');
    } else {
      this.initDatadog(datadogOptions);
    }
  }

  isDatadogInitialized() {
    // @ts-ignore
    const rumContext = window.DD_RUM?.getInternalContext();
    return !!rumContext?.applicationId;
  }

  initDatadog(datadogOptions: RumInitConfiguration) {
    datadogRum.init({
      sessionSampleRate: 20,
      sessionReplaySampleRate: 0,
      trackUserInteractions: false,
      allowedTracingUrls: [window.location.origin],
      site: 'datadoghq.com',
      startSessionReplayRecordingManually: true,
      ...datadogOptions,
      beforeSend: (event: RumEvent, context: RumEventDomainContext) => {
        this.reportWebVitals(event.view);
        if (datadogOptions.beforeSend) {
          return datadogOptions.beforeSend?.(event, context);
        }
        return true;
      },
    });
  }

  reportWebVitals(view: RumEvent['view']) {
    if (this.gauge) {
      Object.keys(this.webVitals).forEach((metricKey) => {
        const metricValue = view[metricKey];
        if (typeof metricValue === 'number') {
          delete this.webVitals[metricKey as keyof typeof this.webVitals];
          const metricName = this.getMetricName(metricKey);
          this.gauge?.({
            name: metricName,
            amount: metricValue,
            options: this.alertConfig && {
              alert: {
                ...this.alertConfig,
                metricName,
                metricType: 'GAUGE',
              },
            },
          });
        }
      });
    }
  }

  onInitialize(increment: (i: CountOptions) => void, gauge: (g: MeasureOptions) => void, logger?: Logger) {
    this.gauge = gauge;
    this.logger = this.logger || logger;
  }

  validateRumInitConfig(config: RumInitConfiguration) {
    // only handle errors if on the client
    if (typeof window === 'undefined') return;

    // only these fields will fail silently if they are not the correct type
    // https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters
    const silentFailingFieldTypes = {
      env: 'string',
      service: 'string',
      trackViewsManually: 'boolean',
      version: 'string',
    } as Record<keyof RumInitConfiguration, string>;

    const validationErrors: Array<{ field: string; expectedType: string; receivedType: string }> = [];
    (Object.keys(silentFailingFieldTypes) as Array<keyof RumInitConfiguration>).forEach((field) => {
      const expectedType = silentFailingFieldTypes[field];

      if (typeof config[field] !== 'undefined' && typeof config[field] !== expectedType) {
        validationErrors.push({ field, expectedType, receivedType: typeof config[field] });
      }
    });

    if (!validationErrors.length) return;

    const errorMessage = `Invalid type(s) provided to \`DatadogPlugin\`\n${JSON.stringify(validationErrors, null, 2)}`;
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || /^[0-9.]+$/.test(hostname);

    if (isLocalhost) {
      throw new Error(errorMessage);
    } else {
      // prevent app from crashing in non-localhost environments but still log the error so devs can debug in any env
      // eslint-disable-next-line no-console
      console.error(errorMessage);
    }
  }
}
